import { KeyboardEvent, useContext, useRef, useState } from 'react';
import type { Editor, JSONContent, Range } from '@tiptap/core';
import { Caption1, Input, MenuGroup, MenuGroupHeader, MenuItem, MenuList } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';

import DocumentContext from '../../contexts/DocumentContext.context';
import {
  getParagraphInsertPos,
  getRangeAfterBlock,
  insertFormField,
  insertParagraphField,
} from '../../tiptap/nodes/formField/formFieldHelpers';
import { ETiptapSelectorKey } from '../../types/tiptap.types';
import { FORM_FIELD_ITEMS, PARAGRAPH_FIELD_ITEMS, TEXT_BLOCK_ITEMS, TInsertItem } from './constants';

type TEntry = TInsertItem & {
  key: string;
  group: string;
  insert: () => void;
};

type TProps = {
  blockPos: number;
  onDone: () => void;
};

const matchesQuery = ({ title }: TEntry, query: string) => title.toLowerCase().includes(query.trim().toLowerCase());

const insertTextBlock = (editor: Editor, range: Range, block: JSONContent) =>
  editor
    .chain()
    .insertContentAt(range, block)
    .setTextSelection(range.from + 1)
    .focus()
    .run();

/** A block's + menu: a card or text below the block, or for a paragraph also a field in its text, from one search. */
const InsertFieldMenu = ({ blockPos, onDone }: TProps) => {
  const { editor, templateDropdownOptionsRef } = useContext(DocumentContext);
  const [query, setQuery] = useState('');
  const refFirstItem = useRef<HTMLDivElement>(null);
  const isParagraph = editor.state.doc.nodeAt(blockPos)?.type.name === ETiptapSelectorKey.PARAGRAPH;
  const rangeAfterBlock = () => getRangeAfterBlock(editor.state.doc, blockPos);

  // One list in the order shown, with the groups top to bottom.
  const entries: TEntry[] = [
    ...FORM_FIELD_ITEMS.map((item) => ({
      ...item,
      key: item.fieldName,
      group: 'Form fields',
      insert: () => insertFormField(editor, rangeAfterBlock(), item.fieldName, templateDropdownOptionsRef),
    })),
    ...(isParagraph
      ? PARAGRAPH_FIELD_ITEMS.map((item) => ({
          ...item,
          key: `paragraph-${item.fieldName}`,
          group: 'In this paragraph',
          insert: () =>
            insertParagraphField(
              editor,
              item.fieldName,
              templateDropdownOptionsRef,
              getParagraphInsertPos(editor.state, blockPos)
            ),
        }))
      : []),
    ...TEXT_BLOCK_ITEMS.map((item) => ({
      ...item,
      key: item.title,
      group: 'Text',
      insert: () => insertTextBlock(editor, rangeAfterBlock(), item.block),
    })),
  ].filter((entry) => matchesQuery(entry, query));
  const groups = [...new Set(entries.map(({ group }) => group))];

  const pick = (entry: TEntry) => {
    onDone();
    entry.insert();
  };

  // The arrows go on into the list, which moves between its items; Enter takes the first match.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') refFirstItem.current?.focus();
    else if (event.key === 'Enter' && entries[0]) pick(entries[0]);
    else return;
    event.preventDefault();
  };

  return (
    <>
      <Input
        autoFocus
        className="mb-1"
        contentBefore={<SearchRegular />}
        placeholder="Search fields and text"
        value={query}
        onChange={(_, data) => setQuery(data.value)}
        onKeyDown={onKeyDown}
      />
      <MenuList>
        {groups.map((group) => (
          <MenuGroup key={group}>
            <MenuGroupHeader>{group}</MenuGroupHeader>
            {entries.map((entry, index) => {
              if (entry.group !== group) return null;
              const Icon = entry.icon;
              return (
                <MenuItem
                  key={entry.key}
                  ref={index === 0 ? refFirstItem : undefined}
                  icon={<Icon />}
                  onClick={() => pick(entry)}
                >
                  {entry.title}
                </MenuItem>
              );
            })}
          </MenuGroup>
        ))}
      </MenuList>
      {!entries.length && <Caption1 className="px-2 py-3 text-neutralFg-3">Nothing matches “{query}”.</Caption1>}
    </>
  );
};

export default InsertFieldMenu;
